import { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { type AbortResetClient, useAbortController } from 'commons/customHooks'
import { Alert, LoadingModal, Page } from 'components'
import { TypesValidations } from 'constants/enums'
import { URL_CONST } from 'constants/urls'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { asyncGetMaterialById, type MaterialByIdResponse } from 'store/ProductContext'
import { asyncGetCorpoRascunho } from 'store/SalesOrderContext/salesOrderSlice'
import {
  setSalesOrderInitialTab,
  setShowDetailedStock,
} from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'
import * as yup from 'yup'

import { BOX_TYPE } from './components/ProductDetail'
import ProductHeader from './components/ProductHeader'
import ProductItems from './components/ProductItems'
import * as S from './styles'

export type PairBox = 'caixas' | 'pares'

export interface ProductContextState {
  [x: string]: { [y: string]: number } | boolean | number | string | string[]
}

export type PartialContext = `[${string}].${string}`

interface ProductPageProp extends AbortResetClient {
  materialSelected: MaterialByIdResponse
  defaultSizes: Record<string, string | string[] | boolean | Record<string, number>>
}

const ProductPage = ({ materialSelected, defaultSizes, resetController }: ProductPageProp) => {
  const navigate = useNavigate()
  const { orderNumber, materialNumber } = useParams()
  const convertAmount = Number(materialSelected.qt_item_conv)

  const methods = useForm({
    defaultValues: { ...defaultSizes },
    mode: 'onChange',
    resolver: yupResolver(createSchema()),
    reValidateMode: 'onBlur',
  })

  const { control, setValue, clearErrors } = methods

  const watchMixedBox = useWatch({
    control: control,
    name: 'fl_caix_grad',
  }) as boolean

  useEffect(() => {
    if (watchMixedBox) {
      clearErrors()
    }
  }, [watchMixedBox])

  function createSchema() {
    return yup.object().shape(
      materialSelected?.cores.reduce(
        (acc, key) => ({
          ...acc,
          [key.ds_cor]: yup.object().shape(
            key.tamanhos.reduce(
              (acc, tamanho) => ({
                ...acc,
                [tamanho.cd_tamh]: yup
                  .number()
                  .test('is-multiple', 'esse numero não é multiplo', (value) => {
                    if (
                      materialSelected.fl_verf_sku === TypesValidations.YES &&
                      materialSelected.ds_tipo_caix === BOX_TYPE.CLOSED &&
                      !watchMixedBox
                    ) {
                      return value ? value % convertAmount === 0 : true
                    } else {
                      return true
                    }
                  }),
              }),
              {},
            ),
          ),
        }),
        {},
      ),
    )
  }

  useEffect(() => {
    if (
      materialSelected &&
      Object.keys(materialSelected).length &&
      !materialSelected.cores.length &&
      orderNumber
    ) {
      navigate(`${URL_CONST.MANAGE_ORDER}/${orderNumber}`)
      toast.error(`Material ${materialNumber} bloqueado ou não encontrado no banco de dados.`)
    }
    if (materialSelected.fl_digi_caix_vari === TypesValidations.YES) {
      const materialDirtyFields: string[] = materialSelected.cores.reduce((acc, material) => {
        const { qt_tota_cor, ds_cor } = material
        if (qt_tota_cor) return [...acc, ds_cor]
        return acc
      }, [] as string[])

      setValue('fl_caix_grad', materialSelected.fl_caix_grad === TypesValidations.YES)
      setValue(
        'qt_caix_grad',
        materialSelected.qt_caix_grad ? String(materialSelected.qt_caix_grad) : '',
      )
      setValue('disabled_fl_caix_grad', materialDirtyFields.length > 1 ? true : false)
      setValue('dirtyFieldsByColor', materialDirtyFields)
    }
  }, [materialSelected])

  return (
    <Page fullwidth>
      <FormProvider {...methods}>
        <ProductHeader resetController={resetController} />
        <Alert
          fullWidth
          kind="warning"
          title="Os materiais possuem estoque dinâmico, e para garantir a disponibilidade, é necessário enviar as OVs ao SAP."
        />
        <S.WrapperItems>
          <ProductItems
            convertAmount={convertAmount}
            defaultSizes={defaultSizes}
            materialSelected={materialSelected}
          />
        </S.WrapperItems>
      </FormProvider>
    </Page>
  )
}

function AwaitProductPage() {
  const dispatch = useDispatch()
  const { resetController } = useAbortController()
  const { orderNumber, materialNumber } = useParams()
  const { materialSelected, defaultParams, loadingProduct } = useSelector((state) => state.products)
  const {
    draftBody: { dt_dsjd_pedi },
  } = useSelector((state) => state.salesOrder)

  const materialDirtyFields: string[] = materialSelected?.cores?.reduce((acc, material) => {
    const { qt_tota_cor, ds_cor } = material
    if (qt_tota_cor) return [...acc, ds_cor]
    return acc
  }, [] as string[])

  type IsMixedBox = {
    fl_caix_grad: boolean
    qt_caix_grad: string
    disabled_fl_caix_grad: boolean
    dirtyFieldsByColor: string[]
    cd_cent_matr: string
    percentualDesconto: string
  }

  const isMixedBox: IsMixedBox | Pick<IsMixedBox, 'cd_cent_matr' | 'percentualDesconto'> =
    materialSelected.fl_digi_caix_vari === TypesValidations.YES
      ? {
          cd_cent_matr: materialSelected?.cd_cent_matr ?? '',
          dirtyFieldsByColor: materialDirtyFields,
          disabled_fl_caix_grad: materialDirtyFields.length > 1,
          fl_caix_grad: materialSelected.fl_caix_grad === TypesValidations.YES,
          percentualDesconto: '0',
          qt_caix_grad: materialSelected.qt_caix_grad ? String(materialSelected.qt_caix_grad) : '',
        }
      : {
          cd_cent_matr: materialSelected?.cd_cent_matr ?? '',
          percentualDesconto: '0',
        }

  const defaultSizes = materialSelected?.cores?.reduce(
    (acc, key) => ({
      ...acc,
      [key.ds_cor]: key.tamanhos.reduce(
        (acc, tamanho) => ({
          ...acc,
          [tamanho.cd_tamh]:
            materialSelected.ds_tipo_caix === BOX_TYPE.CLOSED
              ? Number(tamanho.qt_item_conv)
              : Number(tamanho.qt_item),
        }),
        {},
      ),
    }),
    {
      ...isMixedBox,
    },
  )

  function getMaterial() {
    const { controller } = resetController()

    dispatch(
      asyncGetMaterialById(
        {
          ...defaultParams,
          cd_cent_matr: defaultParams.cd_cent_matr,
          cd_matr: materialNumber,
          dt_dsjd_pedi,
          nr_pedi_rasc: orderNumber,
        },
        '1',
        controller.signal,
      ),
    )
  }

  useEffect(() => {
    dispatch(setSalesOrderInitialTab(1))
    dispatch(setShowDetailedStock(true))
  }, [])

  useEffect(() => {
    if (orderNumber) {
      if (!defaultParams?.cd_empr) {
        dispatch(asyncGetCorpoRascunho(orderNumber))
      }

      if (materialNumber && defaultParams.nr_pedi_rasc) {
        getMaterial()
      }
    }
  }, [defaultParams?.nr_pedi_rasc])

  return (
    <>
      {defaultSizes &&
      !loadingProduct &&
      (Object.entries(defaultSizes).length > 0 || !materialSelected.cores.length) ? (
        <ProductPage
          defaultSizes={defaultSizes}
          materialSelected={materialSelected}
          resetController={resetController}
        />
      ) : (
        <LoadingModal />
      )}
    </>
  )
}

export default AwaitProductPage
